import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

// Exportacion a PDF de una pestaña de Obras: "lo que se ve en la web es lo que
// sale en el PDF". Se captura el DOM real de la pestaña, pero en una copia
// (onclone) donde los contenedores con scroll interno se despliegan, para que
// entren TODAS las filas/obras y no solo la parte visible. Encima se añade una
// cabecera de trazabilidad (vista, fecha/hora, filtros aplicados).

const CAPTURE_CLASS = "obras-pdf-capture";
const PAGE_MARGIN_PT = 18;

// Nombre de fichero estable: Obras_<vista>_<aaaa-mm-dd_hhmm>.pdf
export function pdfFileName(viewLabel, now = new Date()) {
    const pad = (value) => String(value).padStart(2, "0");
    const slug = String(viewLabel || "vista")
        .normalize("NFD").replace(/[̀-ͯ]/g, "")
        .replace(/[^A-Za-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "vista";
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
    return `Obras_${slug}_${stamp}.pdf`;
}

// Trocea una imagen alta en paginas: devuelve [{ y, height }] en pixeles del canvas.
export function pageSlices(totalHeightPx, pageHeightPx) {
    if (!(totalHeightPx > 0) || !(pageHeightPx > 0)) return [];
    const slices = [];
    for (let y = 0; y < totalHeightPx; y += pageHeightPx) {
        slices.push({ y, height: Math.min(pageHeightPx, totalHeightPx - y) });
    }
    return slices;
}

// En la copia: quita alturas maximas y scrolls internos para que el contenido se
// despliegue entero. Se hace con estilos inline porque el clon ya tiene el CSS.
function expandScrollAreas(root, win) {
    // Los bloques ocultos (la hoja montada tras otra pestaña) no se recorren.
    const nodes = [root, ...root.querySelectorAll("*")].filter((node) => !node.closest("[hidden]"));
    nodes.forEach((node) => {
        const style = win.getComputedStyle(node);
        const scrollsY = ["auto", "scroll"].includes(style.overflowY);
        const scrollsX = ["auto", "scroll"].includes(style.overflowX);
        if (scrollsY || scrollsX || node === root) {
            node.style.setProperty("overflow", "visible", "important");
            node.style.setProperty("height", "auto", "important");
            node.style.setProperty("max-height", "none", "important");
        }
        if (style.position === "sticky") node.style.setProperty("position", "static", "important");
    });
    // La pestaña es un hijo flex encogible dentro de una pagina de alto fijo: en la
    // copia se liberan ella y sus ancestros para que tome su alto natural.
    root.style.setProperty("flex", "none", "important");
    for (let node = root.parentElement; node && node !== root.ownerDocument.documentElement; node = node.parentElement) {
        node.style.setProperty("height", "auto", "important");
        node.style.setProperty("max-height", "none", "important");
        node.style.setProperty("overflow", "visible", "important");
    }
}

function buildHeader(doc, { title, subtitle, filtersLine, now }) {
    const header = doc.createElement("div");
    header.style.cssText = "font-family:Mulish,Arial,sans-serif;color:#102a43;padding:0 0 12px;margin:0 0 12px;border-bottom:1px solid #d9e2ec;";
    const stamp = now.toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
    const line = (text, css) => {
        const el = doc.createElement("div");
        el.textContent = text;
        el.style.cssText = css;
        header.appendChild(el);
    };
    line(title, "font-size:18px;font-weight:800;");
    if (subtitle) line(subtitle, "font-size:12px;color:#486581;margin-top:2px;");
    line(`Exportado ${stamp} · Filtros: ${filtersLine || "Sin filtros aplicados"}`, "font-size:11px;color:#627d98;margin-top:4px;");
    return header;
}

export async function exportElementToPdf(element, { viewLabel, title, subtitle, filtersLine, now = new Date() } = {}) {
    if (!element) throw new Error("No hay contenido que exportar");
    const marker = `pdf-${now.getTime()}`;
    element.setAttribute("data-pdf-root", marker);
    try {
        const canvas = await html2canvas(element, {
            scale: 2,
            backgroundColor: "#ffffff",
            useCORS: true,
            logging: false,
            windowWidth: Math.max(element.scrollWidth, 1280),
            windowHeight: Math.max(element.scrollHeight, 900),
            onclone: (clonedDoc) => {
                const root = clonedDoc.querySelector(`[data-pdf-root="${marker}"]`);
                if (!root) return;
                root.classList.add(CAPTURE_CLASS);
                expandScrollAreas(root, clonedDoc.defaultView);
                root.style.setProperty("padding", "16px", "important");
                root.style.setProperty("background", "#ffffff", "important");
                root.insertBefore(buildHeader(clonedDoc, { title, subtitle, filtersLine, now }), root.firstChild);
            },
        });

        const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
        const pageW = pdf.internal.pageSize.getWidth() - PAGE_MARGIN_PT * 2;
        const pageH = pdf.internal.pageSize.getHeight() - PAGE_MARGIN_PT * 2;
        const pxPerPt = canvas.width / pageW;
        const slices = pageSlices(canvas.height, Math.floor(pageH * pxPerPt));
        slices.forEach((slice, index) => {
            const page = document.createElement("canvas");
            page.width = canvas.width;
            page.height = slice.height;
            page.getContext("2d").drawImage(canvas, 0, slice.y, canvas.width, slice.height, 0, 0, canvas.width, slice.height);
            if (index > 0) pdf.addPage();
            pdf.addImage(page.toDataURL("image/jpeg", 0.9), "JPEG", PAGE_MARGIN_PT, PAGE_MARGIN_PT, pageW, slice.height / pxPerPt);
        });
        pdf.save(pdfFileName(viewLabel || title, now));
        return { pages: slices.length };
    } finally {
        element.removeAttribute("data-pdf-root");
    }
}
